// Small DOM helpers shared by the page scripts.
export const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id)! as T;

/** An element with props (`class`, `on<event>` listeners, `data-*` attributes, any other property) and children. */
export function h<K extends keyof HTMLElementTagNameMap>(tag: K, props: Record<string, any> = {}, ...children: (Node | string | null)[]) {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(props)) {
    if (k.startsWith("on")) el.addEventListener(k.slice(2), v);
    else if (k === "class") el.className = v;
    // an attribute: as a property, `data-since` would be set but never found by `[data-since]` or `dataset`
    else if (k.startsWith("data-")) el.setAttribute(k, String(v));
    else (el as any)[k] = v;
  }
  for (const c of children) if (c != null) el.append(c);
  return el;
}
/**
 * Run `tick` every `ms` while the page is visible, and right away when it becomes visible or gets the focus again: a
 * clock on screen that is right the moment one looks, and costs nothing in a tab in the background.
 */
export function whileVisible(ms: number, tick: () => void) {
  let timer: number | undefined;
  const sync = () => {
    window.clearInterval(timer);
    timer = undefined;
    if (document.visibilityState !== "visible") return;
    tick();
    timer = window.setInterval(tick, ms);
  };
  document.addEventListener("visibilitychange", sync);
  window.addEventListener("focus", sync);
  sync();
}
/** An element with an optional class and text. */
export const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls = "", text = "") => h(tag, cls ? { class: cls } : {}, text || null);

/** Feedback or status (`.flash`): `ok` green, `bad` red, none neutral. Other classes on `target` stay. Returns it. */
export const flash = <T extends HTMLElement>(target: T, text: string, kind?: "ok" | "bad") => {
  target.classList.add("flash"); target.classList.toggle("ok", kind === "ok"); target.classList.toggle("bad", kind === "bad");
  target.textContent = text; return target;
};

/**
 * A settings form that saves on every change without a page load, silently: only a failed save shows `error` (its
 * `Flash`, with the form's `data-failed` text), and the next save that works hides it again.
 */
export function autosaveForm(form: HTMLFormElement, error: HTMLElement | null, onSaved?: (changed: Element) => void) {
  form.addEventListener("change", async (e) => {
    // a required field left empty isn't sent: the browser points at it instead
    if (!form.checkValidity()) { form.reportValidity(); return; }
    try {
      const r = await fetch(form.action, { method: "POST", body: new FormData(form) });
      if (!r.ok) throw new Error(String(r.status));
      if (error) error.hidden = true;
      onSaved?.(e.target as Element);
    } catch {
      if (error) { error.textContent = form.dataset.failed ?? ""; error.hidden = false; }
    }
  });
}

/**
 * A spoken clip: `play` starts it from the start, `preload` fetches it before the first play. A male clip draws the
 * voice Easter egg on the server for every playback (ADR 0125): once a playback starts, the next one's clip is asked
 * for (ETag, so a clip the browser already has costs a 304, not a download) and is ready when the next tap comes,
 * which must start the audio itself (iOS). Other clips come straight from the browser's cache.
 */
export function clipPlayer(url: string, preload: "auto" | "none" = "none") {
  const a = Object.assign(new Audio(url), { preload });
  let next: string | null = null, shown: string | null = null, asking = false;
  a.addEventListener("playing", () => {
    if (asking) return;
    asking = true;
    fetch(url, { cache: "no-cache" }).then((r) => (r.ok ? r.blob() : null)).then((b) => {
      if (!b) return;
      if (next) URL.revokeObjectURL(next);
      next = URL.createObjectURL(b);
    }).catch(() => {}).finally(() => { asking = false; });
  });
  return {
    preload: () => a.load(),
    play(rate = 1) {
      a.pause();
      if (next) { if (shown) URL.revokeObjectURL(shown); a.src = shown = next; next = null; } else a.currentTime = 0;
      a.playbackRate = rate;
      a.play().catch(() => {});
    },
  };
}
